import React, { Fragment, useEffect, useState } from 'react'
import { PositionAlertDetailsHeader } from './PositionAlertDetailsHeader'
import { useApi } from '../../../hooks/useApi'
import { State } from '../../_EOS/State'
import { Status } from '../../../hooks/useFetchState'
import {
  Box,
  Button,
  Table,
  TableContainer,
  Typography
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import {
  PositionAlert,
  PositionAlertDetail
} from '../../../interfaces/PositionAlerts/PositionAlerts'
import { Modal } from '../../_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { AddAlertModal } from '../Modals/AddAlertModal'
import { usePagination } from '../../../hooks/usePagination'
import { ApiResponse } from '../../../typings/ApiResponse'
import { SnackBar } from '../../SnackBar/Snackbar'
import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  getPositionAlertDetails,
  patchPositionAlertDetailResolveMassive,
  patchPositionAlertId,
  patchResolvePositionAlertDetail
} from '../config/api'
import { Pagination } from '../../Pagination'
import { LoadingButton } from '../../_EOS/Button/LoadingButton'
import { PositionAlertTableHeader } from './table/PositionAlertTableHeader'
import { PositionAlertTableBody } from './table/PositionAlertTableBody'
import { eTypeAlert } from '../../../enums/eTypeAlert'

interface PositionAlertsDetailsProps {
  positionAlert: PositionAlert
}

interface AddAlertModalProps {
  positionCode: string
  positionAlertQuantity: number
  sku: string
  positionAlertDetailId: number
  flowId: number
  externalStorage: boolean
  typePositionId: number
  batchId?: number | null
  batchNumber?: string | null
  expirationDate?: Date | null
  expirationStatus?: string | null
}

export const PositionAlertsDetails: React.FC<PositionAlertsDetailsProps> = ({
  positionAlert: {
    positionAlertId,
    warehouseName,
    zoneName,
    positionName,
    typePositionName
  }
}) => {
  const { state, callApi } = useApi<ApiResponse<PositionAlertDetail>>()
  const {
    state: patchResolveIncidentState,
    callApi: patchResolveIncidentCallApi
  } = useApi<ApiResponse<PositionAlertDetail>>()

  const snackbarState = useSnackbar()

  const pagination = usePagination(5)

  const [checkedRevisionElements, setCheckedRevisionElements] = useState<
    number[]
  >([])

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestGetPositionAlertDetails(auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  // Estado de modal para agregar alertas de inventario
  const addAlertModal = useModal<AddAlertModalProps>(
    'Incidencias de Inventario'
  )

  const confirmModal = useModal<{ message: string; onConfirm: () => void }>(
    'Confirmación'
  )

  const classes = useStyles()

  const requestGetPositionAlertDetails = (page?: number, pageSize?: number) => {
    callApi(getPositionAlertDetails(positionAlertId, page, pageSize))
  }

  const requestPatchIncidence = (positionAlertDetailId: number) => {
    patchResolveIncidentCallApi(
      patchResolvePositionAlertDetail(positionAlertDetailId)
    )
      .then(() =>
        snackbarState.openSnackBar('Alerta resuelta correctamente', true)
      )
      .then(confirmModal.closeModal)
      .catch((err) =>
        snackbarState.openSnackBar(`Error al resolver alerta: ${err}`, true)
      )
      .finally(() => requestGetPositionAlertDetails(0, pagination.pageSize))
  }

  const requestPatchIncidenceResolveMassive = () => {
    patchResolveIncidentCallApi(
      patchPositionAlertDetailResolveMassive(checkedRevisionElements)
    )
      .then(() =>
        snackbarState.openSnackBar('Alerta resuelta correctamente', true)
      )
      .then(confirmModal.closeModal)
      .catch((err) =>
        snackbarState.openSnackBar(`Error al resolver alerta: ${err}`, true)
      )
      .finally(() => requestGetPositionAlertDetails(0, pagination.pageSize))
  }

  const requestSingleResolveAlert = (positionAlertIds: number[]) => {
    callApi(patchPositionAlertId(positionAlertIds))
      .then(() =>
        snackbarState.openSnackBar('Alerta resuelta correctamente', true)
      )
      .then(confirmModal.closeModal)
      .catch((err) =>
        snackbarState.openSnackBar(`Error al resolver alerta: ${err}`, true)
      )
      .finally(() => requestGetPositionAlertDetails(0, pagination.pageSize))
  }

  useEffect(() => {
    requestGetPositionAlertDetails()
  }, [])

  const handleCloseModal = (message: string, success: boolean) => {
    requestGetPositionAlertDetails()
    addAlertModal.closeModal()
    snackbarState.openSnackBar(message, success)
  }

  const handleShowResolveIncidenceModal = (
    id: number,
    row: PositionAlertDetail
  ) => {
    addAlertModal.openModal('sm', {
      positionCode: positionName || '',
      positionAlertQuantity: row.quantity,
      sku: row.sku,
      positionAlertDetailId: id,
      flowId: row.flowId,
      externalStorage: row.externalStorage,
      typePositionId: row.typePositionId,
      batchId: row.batchId,
      batchNumber: row.batchNumber,
      expirationDate: row.expirationDate,
      expirationStatus: row.expirationStatus
    })
  }

  const handleCheckPositionAlertDetail = (positionAlertDetailId: number) => {
    setCheckedRevisionElements((prevCheckedElements) =>
      prevCheckedElements.includes(positionAlertDetailId)
        ? prevCheckedElements.filter((el) => el !== positionAlertDetailId)
        : [...prevCheckedElements, positionAlertDetailId]
    )
  }

  const shouldInvetoryCheckButtonBeActivated = !(state.data?.items || []).some(
    (el) => el.action === 'RECONTEO'
  )

  return (
    <Fragment>
      <State state={state} loadingMessage='Obteniendo alertas...'>
        <PositionAlertDetailsHeader
          warehouseName={warehouseName || ''}
          zoneName={zoneName || ''}
          positionCode={positionName || ''}
          typePositionName={typePositionName || ''}
        />
        {state.status === Status.Success &&
        (state.data?.items?.length || 0) > 0 ? (
          <Fragment>
            <TableContainer>
              <Table size={'small'}>
                <PositionAlertTableHeader
                  shouldShowFirstHeader={
                    state.data?.items.some(
                      (el) =>
                        el.action === 'REVISIÓN' ||
                        el.typeAlertId === eTypeAlert.External
                    ) || false
                  }
                />
                <PositionAlertTableBody
                  details={state.data?.items}
                  checkedElements={checkedRevisionElements}
                  onCheckElement={handleCheckPositionAlertDetail}
                  onElementDone={requestPatchIncidence}
                  onAdjustElement={handleShowResolveIncidenceModal}
                  confirmModalState={confirmModal}
                />
              </Table>
              <Pagination
                pagination={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  totalItem: state.data?.totalItems || 0
                }}
                onPaginationChange={handlePaginationChange}
              />
            </TableContainer>

            <Box textAlign={'center'}>
              <Button
                disabled={shouldInvetoryCheckButtonBeActivated}
                variant='contained'
                color='primary'
                onClick={() =>
                  confirmModal.openModal('sm', {
                    message:
                      '¿Está seguro que desea generar inventario ciclico?',
                    onConfirm: () =>
                      requestSingleResolveAlert([positionAlertId])
                  })
                }
              >
                Realizar Inventario
              </Button>{' '}
              <LoadingButton
                loading={patchResolveIncidentState.status === Status.Loading}
                variant='outlined'
                color='primary'
                disabled={checkedRevisionElements.length <= 0}
                onClick={() =>
                  confirmModal.openModal('sm', {
                    message:
                      '¿Está seguro que desea generar liberar alertas seleccionadas?',
                    onConfirm: () => requestPatchIncidenceResolveMassive()
                  })
                }
              >
                Liberar
              </LoadingButton>
            </Box>
          </Fragment>
        ) : (
          <div className={classes.paper} style={{ textAlign: 'center' }}>
            <Typography>
              No existen alertas para la posición {positionName}
            </Typography>
          </div>
        )}
      </State>

      <Modal
        open={confirmModal.isOpen}
        onClose={confirmModal.closeModal}
        cancelButton={{ action: confirmModal.closeModal, title: 'Cancelar' }}
        successButton={{
          action: confirmModal.extendedProps?.onConfirm!,
          title: 'Confirmar'
        }}
        loading={
          state.status === Status.Loading ||
          patchResolveIncidentState.status === Status.Loading
        }
      >
        {confirmModal.extendedProps?.message}
      </Modal>

      <Modal
        open={addAlertModal.isOpen}
        onClose={() => {
          addAlertModal.closeModal()
          requestGetPositionAlertDetails()
        }}
        maxWidth={addAlertModal.size}
        title={addAlertModal.title}
      >
        <AlertDetailModal
          extendedProps={addAlertModal.extendedProps}
          handleCloseModal={handleCloseModal}
        />
      </Modal>

      <SnackBar
        open={snackbarState.isActive}
        message={snackbarState.message}
        status={snackbarState.isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}

export const AlertDetailModal: React.FC<{
  extendedProps: AddAlertModalProps | undefined
  handleCloseModal: (message: string, succes: boolean) => void
}> = ({ extendedProps, handleCloseModal }) => {
  return (
    <Fragment>
      {!extendedProps?.positionAlertDetailId && (
        <Typography variant='body2'>
          No se ha seleccionado un detalle de alerta
        </Typography>
      )}
      {extendedProps?.positionAlertDetailId && (
        <AddAlertModal
          positionName={extendedProps.positionCode}
          positionAlertQuantity={extendedProps.positionAlertQuantity}
          sku={extendedProps.sku}
          positionAlertDetailId={extendedProps.positionAlertDetailId}
          flowId={extendedProps.flowId}
          externalStorage={extendedProps.externalStorage}
          typePositionId={extendedProps.typePositionId}
          batchId={extendedProps.batchId}
          batchNumber={extendedProps.batchNumber}
          expirationDate={extendedProps.expirationDate}
          expirationStatus={extendedProps.expirationStatus}
          closeModal={(message: string, success: boolean) =>
            handleCloseModal(message, success)
          }
        />
      )}
    </Fragment>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  paper: {
    overflow: 'auto',
    padding: theme.spacing(2),
    [theme.breakpoints.up('xs')]: {
      maxHeight: '400px' // Limit the vertical size to 80% of the viewport height
    }
  }
}))
